import axios from "axios";
import TenantService from './TenantService';
import UserService from './UserService';
import ConnectionService from './ConnectionService';
import Settings from '../wrappers/Settings';
import { Locale } from "../wrappers";
function isForm(data) {
    return data !== undefined && 'send' in data && typeof data.send === 'function';
}
class ApiService {
    baseURL;
    interceptor401;
    interceptor402;
    http;
    constructor() {
        this.interceptor401 = null;
        this.interceptor402 = null;
        this.http = axios.create({
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'App-Name': 'konty'
            }
        });
        this.mountInterceptorOffline();
    }
    async init(locale) {
        const baseURL = await Locale.getApiUrl(locale);
        this.setBaseUrl(baseURL);
    }
    setBaseUrl(baseURL) {
        this.baseURL = baseURL;
        this.http.defaults.baseURL = this.baseURL;
    }
    getBaseUrl() {
        return this.baseURL;
    }
    /**
     * Set Tenant Authorization Header
     */
    setTenantHeader(token) {
        this.http.defaults.headers.common['TnAuthorization'] = token ? `Tenant ${token}` : undefined;
    }
    /**
     * Set User Authorization Header
     */
    setUserHeader(token) {
        this.http.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : undefined;
    }
    /**
     * Set SAP User Authorization Header
     * Used for Shared Access Portal authentication (separate from tenant/user auth)
     */
    setSapUserHeader(token) {
        this.http.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : undefined;
    }
    /**
     * Remove SAP User Authorization Header
     */
    removeSapUserHeader() {
        delete this.http.defaults.headers.common['Authorization'];
    }
    /**
     * Set Cash Register Header
     */
    setCashRegisterHeader(cashRegisterId) {
        this.http.defaults.headers.common['CashRegister'] = cashRegisterId || undefined;
    }
    /**
     * Set Platform Header
     */
    setDevicePlatformHeader(platform) {
        this.http.defaults.headers.common['Platform'] = platform || undefined;
    }
    /**
     * Set Device Uuid Header
     */
    setDeviceUuidHeader(deviceUuid) {
        this.http.defaults.headers.common['Device'] = deviceUuid || undefined;
    }
    /**
     * Set Echo Socket Id Header
     */
    setSocketIdHeader(socketId) {
        this.http.defaults.headers.common['X-Socket-ID'] = socketId || undefined;
    }
    /**
     * Remove Echo Socket Id Header
     */
    removeSocketIdHeader() {
        delete this.http.defaults.headers.common['X-Socket-ID'];
    }
    /**
     * Remove Tenant Authorization Header
     */
    removeTenantHeader() {
        delete this.http.defaults.headers.common['TnAuthorization'];
    }
    /**
     * Remove User Authorization Header
     */
    removeUserHeader() {
        delete this.http.defaults.headers.common['Authorization'];
    }
    /**
     * Remove Cash Register Header
     */
    removeCashRegisterHeader() {
        delete this.http.defaults.headers.common['CashRegister'];
    }
    /**
     * Remove Platform Header
     */
    removeDevicePlatformHeader() {
        delete this.http.defaults.headers.common['Platform'];
    }
    /**
     * Detect when to activate the offline mode
     */
    mountInterceptorOffline() {
        this.interceptor401 = this.http.interceptors.response.use(response => response, async (error) => {
            const isApiRequest = error.config &&
                error.config.url &&
                !error.config.url.includes('http://') &&
                !error.config.url.includes('http://');
            const isOfflineResponse = (!error.response && !!error.request) ||
                error.message === 'Network Error' ||
                error.response?.status === 503;
            if (isApiRequest && isOfflineResponse) {
                await ConnectionService.setOffline();
            }
            return Promise.reject(error);
        });
    }
    /**
     * Handle the 401 error response
     */
    mountInterceptor401() {
        this.interceptor401 = this.http.interceptors.response.use(response => response, async (error) => {
            if (error.request && error.request.status == 401) {
                await UserService.removeToken();
                await UserService.removeUser();
                if (error.response.data.message == 'tenant_unauthorized') {
                    await TenantService.removeToken();
                    await TenantService.removeTenant();
                    Settings.onUnauthorized(true);
                }
                else {
                    Settings.onUnauthorized(false);
                }
            }
            return Promise.reject(error);
        });
    }
    /**
     * Handle the 402 error response: aria-api refused an inactive tenant (its only 402,
     * CheckSubscriptionsPlans), naming the tenant's status in the body.
     */
    mountInterceptor402() {
        this.interceptor402 = this.http.interceptors.response.use(response => response, error => {
            if (error.request && error.request.status === 402) {
                TenantService.markInactive(error.response?.data?.status);
            }
            return Promise.reject(error);
        });
    }
    /**
     * Remove the 401 interceptor
     */
    unmountInterceptor401() {
        if (!this.interceptor401)
            return;
        this.http.interceptors.response.eject(this.interceptor401);
    }
    /**
     * Remove the 402 interceptor
     */
    unmountInterceptor402() {
        if (!this.interceptor402)
            return;
        this.http.interceptors.response.eject(this.interceptor402);
    }
    async get(url, config) {
        return await this.http.get(url, config);
    }
    async post(url, data, config) {
        if (isForm(data)) {
            return await data.send('post', url, config);
        }
        return await this.http.post(url, data, config);
    }
    async put(url, data, config) {
        if (isForm(data)) {
            return await data.send('put', url, config);
        }
        return await this.http.put(url, data, config);
    }
    async patch(url, data, config) {
        if (isForm(data)) {
            return await data.send('patch', url, config);
        }
        return await this.http.patch(url, data, config);
    }
    async delete(url, config) {
        return await this.http.delete(url, config);
    }
    async customRequest(data) {
        return await this.http(data);
    }
}
;
export const apiService = new ApiService();
export default apiService;
//# sourceMappingURL=ApiService.js.map